import { z } from "zod";

const translationPath = "page.settings.crm.ticketAutoTransferRules.errors.";

const ticketTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_type" },
);

const ticketWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const ticketChannelStepStatusSchema = z.object({
  code: z.string().optional(),
  id: z.string(),
  name: z.string(),
});

const ticketChannelStepSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    status: ticketChannelStepStatusSchema,
  },
  {
    invalid_type_error: translationPath + "ticket_channel_step",
    required_error: translationPath + "ticket_channel_step",
  },
);

const ticketChannelSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
  },
  { invalid_type_error: translationPath + "ticket_channel" },
);

const ticketServiceSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_service" },
);

const ticketServiceAreasSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_service_area" },
);

const ticketAutoTransferRuleHourAtendSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_auto_transfer_rule_hour_atend" },
);

export const TicketAutoTransferRuleSchema = z
  .object({
    id: z.string().optional(),
    transfer: z.boolean(),
    ticketType: ticketTypeSchema,
    ticketChannelStep: ticketChannelStepSchema
      .nullable()
      .refine((val) => val, translationPath + "ticket_channel_step"),
    ticketChannelStepTransfer: ticketChannelStepSchema
      .nullable()
      .refine((val) => val, translationPath + "ticket_channel_step_transfer"),
    ticketChannel: ticketChannelSchema
      .nullable()
      .refine((val) => val, translationPath + "ticket_channel"),
    ticketService: ticketServiceSchema.nullable().optional(),
    ticketServiceAreas: ticketServiceAreasSchema.nullable().optional(),
    ticketAutoTransferRuleHourAtend:
      ticketAutoTransferRuleHourAtendSchema.nullable().optional(),
    ticketChannelTransfer: ticketChannelSchema
      .nullable()
      .refine((val) => val, translationPath + "ticket_channel"),
    ticketWorkspace: ticketWorkspaceSchema
      .optional()
      .nullable()
      .refine((val) => val, translationPath + "ticket_workspace"),
    initialDate: z.date({
      invalid_type_error: translationPath + "initial_date",
    }),
    endDate: z.date({ invalid_type_error: translationPath + "end_date" }),
    minutes: z.string().optional(),
    message: z.string().optional(),
    endOfDay: z.boolean(),
    transferIndifferentPerson: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (val.transfer) {
      if (!val.message || val.message.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["message"],
          message: translationPath + "message",
        });
      }
      if (!val.minutes || val.minutes.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["minutes"],
          message: translationPath + "minutes",
        });
      } else if (Number(val.minutes) > 1440) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["minutes"],
          message: translationPath + "max_minutes",
        });
      }
    }
    if (val.endDate < val.initialDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: translationPath + "end_date_less",
      });
    }
    if (val.ticketService && !val.ticketAutoTransferRuleHourAtend) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticketAutoTransferRuleHourAtend"],
        message: translationPath + "ticket_auto_transfer_rule_hour_atend",
      });
    }
  });

export type TTicketAutoTransferRuleForm = z.infer<
  typeof TicketAutoTransferRuleSchema
>;
